import {ResidueType} from 'biojs/BioConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section} from 'constants/NavConstants';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {Selection} from 'molbio/maps/selectables/Selection';
import * as sequtil from 'molbio/SeqUtil';
import {openListing} from 'nav/sidepanel/SidepanelReduxActions';
import {getStore} from 'redux-stores/Store';
import {ProteinLengthQuery, ProteinLengthQueryVariables} from 'search/blast/__generated/ProteinLengthQuery';
import {
  SequenceLengthQuery,
  SequenceLengthQueryVariables,
} from 'search/blast/__generated/SequenceLengthQuery';
import {StepKey} from 'search/blast/BLASTModal';
import BLASTModalForSelection from 'search/blast/BLASTModalForSelection';
import {PROTEIN_LENGTH_QUERY, SEQUENCE_LENGTH_QUERY} from 'search/blast/BLASTModalQuery';
import {BlastModalTrigger, trackBlastModalOpen} from 'search/blast/BLASTTracking';
import * as FilterUtil from 'search/FilterUtil';
import {getFilterByKey} from 'search/FilterUtil';
import {
  BLASTFilter,
  BLASTOperator,
  BLASTTextSequence,
  Filter,
  FilterErrors,
  FilterKey,
  MultiValueFilter,
  Operator,
  OPERATOR_TO_TEXT,
  SearchParams,
} from 'search/SearchConstants';
import {mergeFiltersToSearchParams} from 'search/SearchUtil';
import runModal from 'ui-library/runModal';

export const BLASTN_TYPES = [
  ModelTypeConstants.DNA_OLIGO,
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.RNA_SEQUENCE,
  ModelTypeConstants.RNA_OLIGO,
];
export const BLASTP_TYPES = [ModelTypeConstants.PROTEIN];

export function hasNucleotideInput(operator: BLASTOperator): boolean {
  return operator === Operator.BLASTN || operator === Operator.BLASTX || operator === Operator.TBLASTX;
}

export function hasNucleotideOutput(operator: BLASTOperator): boolean {
  return operator === Operator.BLASTN || operator === Operator.TBLASTN || operator === Operator.TBLASTX;
}

export function mergeBLASTFilter(searchParams: SearchParams, newBLASTFilter: BLASTFilter): SearchParams {
  const algorithmTypes = hasNucleotideOutput(newBLASTFilter.operator as BLASTOperator)
    ? BLASTN_TYPES
    : BLASTP_TYPES;
  // If the user has already specified a subset of types supproted by the chosen algorithm, we want to
  // preserve that selection. If there are no valid types currently selected, switch types to the full
  // list of types for this algorithm. (This should never occur since the BLAST button is hidden when the
  // selected types don't support it, but guarding against unexpected behavior.)
  const currentTypesFilter = FilterUtil.getFilterByKey(
    searchParams.filters,
    FilterKey.TYPES
  ) as MultiValueFilter | null;
  // This should generally be populated, but defaulting to an empty list of types if a types filter wasn't
  // found just to be safe.
  const currentTypes = currentTypesFilter?.value ?? [];
  const overlappingTypes = currentTypes.filter((type) => (algorithmTypes as Array<string>).includes(type));
  const types = overlappingTypes.length ? overlappingTypes : algorithmTypes;

  return mergeFiltersToSearchParams(searchParams, [
    {
      key: FilterKey.TYPES,
      operator: Operator.IS_ONE_OF,
      value: types,
    },
    newBLASTFilter,
  ]);
}

export function isBLASTSearch(searchParams: SearchParams): boolean {
  return Boolean(getFilterByKey(searchParams.filters, FilterKey.BLAST));
}

export function isMultiQueryBLAST(blastFilter: BLASTFilter): boolean {
  // If the BLASTFilter has multiple queries, add a group by BLAST_QUERY param. Since BLAST
  // always returns the results, and BLAST queries are potentially slow, fetch children immedaitely,
  // unlike the standard group by flow. Since the groups are constrained by the number of queries,
  // this will generally be a small number of groups, so shouldn't cause perf issues due to fetching
  // too many items. Note that if the BLASTFilter is removed or its queries are reduced to 1,
  // availableGroupKeysForFilters won't include BLAST_QUERY, so the groupBy will get cleared
  // above.
  return blastFilter.value.apiIdentifiers.length + blastFilter.value.textSequences.length > 1;
}

export function validateBLASTFilter(filters: Array<Filter>): FilterErrors {
  const typesFilter = FilterUtil.getFilterByKey(filters, FilterKey.TYPES) as MultiValueFilter;

  return filters.map((filter) => {
    if (filter.key === FilterKey.BLAST) {
      const validTypes = hasNucleotideOutput(filter.operator as BLASTOperator) ? BLASTN_TYPES : BLASTP_TYPES;
      return typesFilter?.value.every((type) => (validTypes as Array<string>).includes(type))
        ? null
        : 'The BLAST algorithm chosen is not supported for some selected types.';
    }
    return null;
  });
}

export async function runBLASTModalForSelection(selection: Selection): Promise<void> {
  const polymer = selection.polymer();
  const searchParams = await runModal(BLASTModalForSelection, {
    initialFilter: {
      key: FilterKey.BLAST,
      operator: selection.residueType() === ResidueType.NUCLEOTIDE ? Operator.BLASTN : Operator.BLASTP,
      value: {textSequences: [], apiIdentifiers: []},
    },
    residueSourceId: polymer.id,
    residueType: selection.residueType(),
    selectedResidues: selection.getSelectionResidues(),
    displayStart: sequtil.modelStartToDisplay(selection.start(), polymer.startIndex()),
    displayEnd: sequtil.modelEndToDisplay(
      selection.assertNotEmpty().end(),
      polymer.length(),
      polymer.startIndex()
    ),
    displayLength: selection.length(),
  });
  if (searchParams != null) {
    trackBlastModalOpen(BlastModalTrigger.SELECTION);
    getStore().dispatch(openListing(Section.SEARCH, searchParams));
  }
}

export function makeSequenceLabel(sequence: string, currentSequences: Array<BLASTTextSequence>): string {
  const prefix = `${sequence.substr(0, 5)}_`;
  let maxSuffix = 0;
  currentSequences.forEach((s) => {
    if (s.name.toLowerCase().startsWith(prefix.toLowerCase())) {
      const nameParts = s.name.split('_');
      if (nameParts.length !== 2) {
        return;
      }
      const suffix = parseInt(nameParts[1], 10);
      if (suffix && suffix > maxSuffix) {
        maxSuffix = suffix;
      }
    }
  });

  return `${prefix}${maxSuffix + 1}`;
}

export function stripWhitespace(sequence: string): string {
  return sequence.replace(/\s/g, '');
}

export function getSelectBlastTypeStepTitle(currentStepKey: StepKey, operator: Operator): string | undefined {
  if (currentStepKey === StepKey.SelectBlastType) {
    return undefined;
  }

  return OPERATOR_TO_TEXT[operator];
}

export function getSelectSequencesStepTitle(
  currentStepKey: StepKey,
  filter: BLASTFilter
): string | undefined {
  if (currentStepKey !== StepKey.SetParameters) {
    return undefined;
  }

  const sequenceCount = filter.value.textSequences.length + filter.value.apiIdentifiers.length;
  return `${sequenceCount} selected`;
}

export async function sequenceLengthQuery(ids: Array<string>): Promise<SequenceLengthQuery> {
  return await doQueryWithoutCache<SequenceLengthQuery, SequenceLengthQueryVariables>(
    {query: SEQUENCE_LENGTH_QUERY},
    {
      variables: {ids},
    }
  );
}

export async function proteinLengthQuery(ids: Array<string>): Promise<ProteinLengthQuery> {
  return await doQueryWithoutCache<ProteinLengthQuery, ProteinLengthQueryVariables>(
    {query: PROTEIN_LENGTH_QUERY},
    {
      variables: {ids},
    }
  );
}

// returns true if all sequences are less than 30 in length, false otherwise
export async function shouldUseShortProgram(
  ids: Array<string>,
  textSequences: Array<BLASTTextSequence>,
  operator: BLASTOperator
): Promise<boolean> {
  if (operator === Operator.BLASTX || operator === Operator.TBLASTN || operator === Operator.TBLASTX) {
    return false;
  }

  for (const textSequence of textSequences) {
    if (textSequence.sequence.length > 30) {
      return false;
    }
  }

  if (operator === Operator.BLASTN) {
    const results = await sequenceLengthQuery(ids);
    return results.sequences.every((s) => (s.__typename === 'SequenceNode' ? s.length < 30 : false));
  } else {
    const results = await proteinLengthQuery(ids);
    return results.proteins.every((p) => (p.__typename === 'ProteinNode' ? p.length < 30 : false));
  }
}
